"use client";

import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import ClinicList from "../../components/ClinicList/ClinicList";
import { useTour } from "../../../appointments/hooks/useTour";
import { cliniqueTourSteps } from "./cliniqueTourConfig";

import {  FAKE_CLINIQUES } from "../../fakedata/clinics";
import "./Clinique.css";

export default function Clinique() {
    const { t } = useTranslation();

    const [searchParams] = useSearchParams();
    const [shouldStartTour, setShouldStartTour] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedClinic, setSelectedClinic] = useState(null);

    // ✅ Check for tour parameters
    useEffect(() => {
        const hasGuide = searchParams.get('guide') === 'true';
        const hasTour = searchParams.get('tour') === 'clinique';

        if (hasGuide || hasTour) {

            setTimeout(() => {
                const searchInput = document.querySelector('input[placeholder*="Search"]');
                const clinicList = document.querySelector('.clinique-list-scroll');


                if (searchInput && clinicList) {
                    setShouldStartTour(true);
                } else {
                    console.warn('⚠️ Elements not ready, retrying...');
                    setTimeout(() => setShouldStartTour(true), 1500);
                }
            }, 1000);
        }
    }, [searchParams]);

    // Initialize tour
    useTour(shouldStartTour, cliniqueTourSteps);

    // Filtrage par texte (nom/adresse/vicinity)
    const filtered = useMemo(() => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return FAKE_CLINIQUES;
        return FAKE_CLINIQUES.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.address || "").toLowerCase().includes(q) ||
            (c.vicinity || "").toLowerCase().includes(q)
        );
    }, [searchQuery]);

    // Fonction pour gérer le clic sur une clinique
    const handleClinicClick = (clinic) => {
        // Si on clique sur la même clinique, on la ferme
        if (selectedClinic?.id === clinic.id) {
            setSelectedClinic(null);
        } else {
            setSelectedClinic(clinic);
            // Scroll vers le composant LinkClinic après un court délai
            setTimeout(() => {
                const linkClinicElement = document.getElementById('link-clinic-details');
                if (linkClinicElement) {
                    linkClinicElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 100);
        }
    };

    // Convertir les données de clinique vers le format LinkClinic
    const getClinicData = (clinic) => {
        if (!clinic) return null;

        const defaultServices = t("locators.clinic.defaultServices", { returnObjects: true });

        return {
            logo: clinic.logo || null,
            name: clinic.name,
            // Toujours traduit via i18n pour toutes les langues
            type: t("locators.clinic.fallbackType"),
            website: clinic.website,

            // Texte "Pourquoi choisir" toujours issu de i18n (EN/FR/AR)
            whyChoose: {
                title: t("locators.clinic.whyChooseTitle", { name: clinic.name }),
                description: t("locators.clinic.whyChooseDescription", {
                    name: clinic.name,
                    vicinity: clinic.vicinity || clinic.address || "",
                }),
            },

            images:
                clinic.images || [
                    "https://via.placeholder.com/940x400/E0F2FE/3F9CC6?text=Clinic+Exterior",
                    "https://via.placeholder.com/940x400/DBEAFE/3F9CC6?text=Reception+Area",
                ],

            // "Services proposés" génériques mais traduits (EN/FR/AR)
            offeredServices: {
                title: t("locators.clinic.offeredServicesTitle"),
                description: t("locators.clinic.offeredServicesDescription", {
                    name: clinic.name,
                }),
                services: defaultServices,
            },

            // Bloc contacts : structure issue des fakedata, textes génériques via i18n
            contacts: {
                title: t("locators.clinic.contactsTitle"),
                description: t("locators.clinic.contactsDescription"),
                phone: clinic.contacts?.phone || clinic.phone || "N/A",
                email:
                    clinic.contacts?.email ||
                    clinic.email ||
                    `contact@${clinic.name.toLowerCase().replace(/\s+/g, "")}.tn`,
                address: clinic.contacts?.address || clinic.address,
	                hours:
	                    clinic.contacts?.hours === "Open 24/7 - Seven days a week"
	                        ? t("locators.clinic.hours24_7")
	                        : clinic.contacts?.hours ||
	                          (clinic.opening_hours
	                              ? clinic.opening_hours.join(" | ")
	                              : t("locators.clinic.contactsHoursFallback")),
                mapUrl:
                    clinic.contacts?.mapUrl ||
                    `https://maps.google.com/?q=${encodeURIComponent(
                        clinic.name + " " + clinic.address
                    )}`,
            },
        };
    };

    return (
        <AppLayout>
            <main className="clinique-main clinique--listOnly">
                <div className="clinique-container">
                    <PharmacyHeader
                        title={t("locators.clinic.title")}
                        subtitle={t("locators.clinic.subtitle")}
                        enableFilters={false}
                        showTabs={false}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        activeTab="Clinic"
                        setActiveTab={() => {}}
                    />

                    <section className="clinique-list-card">
                        <div className="clinique-list-scroll">
                            {filtered.length === 0 ? (
                                    <p className="clinique-empty">
                                        {t("locators.clinic.empty")}
                                    </p>
                            ) : (
                                <ClinicList
                                    clinics={filtered}
                                    onClinicClick={handleClinicClick}
                                    selectedClinic={selectedClinic}
                                    getClinicData={getClinicData}
                                />
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </AppLayout>
    );
}